import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  TextField,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import {
  saveTrainerQuestions,
  loadTrainerVideo,
  loadTrainerImage,
} from "../../redux/asyncThunk/userActions";
import { userSlice } from "../../redux/user/userSlice";
import InputFileUpload from "../UploadFile";
import "./mobile-editor.css";

const { addMessage } = userSlice;

function createEmptyQuestion() {
  const id = `q-${Date.now()}`;
  return {
    id,
    questionMode: "text",
    questionText: "",
    hasVideo: false,
    hasImage: false,
    screenpath: "",
    imagepath: "",
    answers: [
      { id: `${id}-a1`, label: "", isCorrect: false },
      { id: `${id}-a2`, label: "", isCorrect: false },
    ],
  };
}

export default function MobileTrainerEditor({ game: initialGame }) {
  const dispatch = useDispatch();
  const game = useSelector((state) => state.user.game) ?? initialGame;

  const [questions, setQuestions] = useState(game?.trainerQuestions ?? []);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isLoadingVideo, setIsLoadingVideo] = useState(false);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  const questionsSaveTimerRef = useRef(null);
  const lastSavedRef = useRef(null);

  const currentQuestion = questions[currentQuestionIndex] || null;
  const currentQuestionMode =
    currentQuestion?.questionMode ??
    (currentQuestion?.hasVideo
      ? "video"
      : currentQuestion?.hasImage
        ? "image"
        : "text");

  // Auto-save
  useEffect(() => {
    if (!game?._id) return;

    const data = JSON.stringify(questions);
    if (data === lastSavedRef.current) return;

    clearTimeout(questionsSaveTimerRef.current);
    questionsSaveTimerRef.current = setTimeout(() => {
      dispatch(saveTrainerQuestions({ gameId: game._id, questions }))
        .unwrap()
        .then(() => {
          lastSavedRef.current = data;
        })
        .catch(() => {
          dispatch(addMessage({ type: "error", message: "Ошибка сохранения" }));
        });
    }, 1500);

    return () => clearTimeout(questionsSaveTimerRef.current);
  }, [questions, game?._id, dispatch]);

  const handleAddQuestion = () => {
    const newQuestion = createEmptyQuestion();
    setQuestions([...questions, newQuestion]);
    setCurrentQuestionIndex(questions.length);
  };

  const handleDeleteQuestion = () => {
    if (questions.length <= 1) {
      dispatch(
        addMessage({
          type: "error",
          message: "Должен быть минимум один вопрос",
        }),
      );
      return;
    }

    const updatedQuestions = questions.filter(
      (_, idx) => idx !== currentQuestionIndex,
    );
    setQuestions(updatedQuestions);

    if (currentQuestionIndex > updatedQuestions.length - 1) {
      setCurrentQuestionIndex(Math.max(0, updatedQuestions.length - 1));
    }

    setShowDeleteConfirm(null);
  };

  const updateQuestion = useCallback(
    (updates) => {
      setQuestions((prevQuestions) =>
        prevQuestions.map((q, idx) =>
          idx === currentQuestionIndex ? { ...q, ...updates } : q,
        ),
      );
    },
    [currentQuestionIndex],
  );

  const updateAnswer = useCallback(
    (answerIndex, updates) => {
      if (!currentQuestion) return;
      const updatedAnswers = currentQuestion.answers.map((a, idx) =>
        idx === answerIndex ? { ...a, ...updates } : a,
      );
      updateQuestion({ answers: updatedAnswers });
    },
    [currentQuestion, updateQuestion],
  );

  const handleVideoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !game?._id || !currentQuestion) return;

    setIsLoadingVideo(true);
    try {
      const result = await dispatch(
        loadTrainerVideo({
          gameId: game._id,
          questionId: currentQuestion.id,
          video: file,
        }),
      ).unwrap();

      updateQuestion({
        hasVideo: true,
        screenpath: result.screenpath,
        questionMode: "video",
      });
    } catch {
      dispatch(addMessage({ type: "error", message: "Ошибка загрузки видео" }));
    } finally {
      setIsLoadingVideo(false);
      event.target.value = "";
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !game?._id || !currentQuestion) return;

    setIsLoadingImage(true);
    try {
      const result = await dispatch(
        loadTrainerImage({
          gameId: game._id,
          questionId: currentQuestion.id,
          image: file,
        }),
      ).unwrap();

      updateQuestion({
        hasImage: true,
        imagepath: result.imagepath,
        questionMode: "image",
      });
    } catch {
      dispatch(
        addMessage({ type: "error", message: "Ошибка загрузки изображения" }),
      );
    } finally {
      setIsLoadingImage(false);
      event.target.value = "";
    }
  };

  if (!currentQuestion) {
    return (
      <div className="mobile-editor mobile-editor--trainer mobile-editor__empty">
        <p>Нет вопросов для редактирования</p>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          className="mobile-editor__btn-add-question"
          onClick={handleAddQuestion}
        >
          Добавить вопрос
        </Button>
      </div>
    );
  }

  return (
    <div className="mobile-editor mobile-editor--trainer">
      <div className="mobile-editor__header mobile-editor__trainer-header">
        <div className="mobile-editor__title">
          <h2>Тренажёр</h2>
        </div>
        <IconButton
          onClick={() => setShowSettings(true)}
          size="small"
          title="Настройки проекта"
          aria-label="Настройки проекта"
        >
          <SettingsOutlinedIcon />
        </IconButton>
      </div>

      <div className="mobile-editor__trainer-layout">
        <aside className="mobile-editor__question-sidebar">
          <h3 className="mobile-editor__section-title">
            Вопросы ({questions.length})
          </h3>
          <div className="mobile-editor__question-list">
            {questions.map((question, index) => {
              const preview =
                question.questionText?.trim() ||
                (question.questionMode === "video"
                  ? "Видео-вопрос"
                  : question.questionMode === "image"
                    ? "Вопрос с изображением"
                    : "Вопрос без текста");

              return (
                <button
                  key={question.id}
                  type="button"
                  className={`mobile-editor__question-choice${
                    index === currentQuestionIndex
                      ? " mobile-editor__question-choice--active"
                      : ""
                  }`}
                  aria-pressed={index === currentQuestionIndex}
                  onClick={() => setCurrentQuestionIndex(index)}
                >
                  <span className="mobile-editor__question-choice-number">
                    {index + 1}
                  </span>
                  <span className="mobile-editor__question-choice-info">
                    <span className="mobile-editor__question-choice-title">
                      Вопрос {index + 1}
                    </span>
                    <span className="mobile-editor__question-choice-preview">
                      {preview}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
          <Button
            fullWidth
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddQuestion}
            className="mobile-editor__btn-add-question"
          >
            Добавить вопрос
          </Button>
        </aside>

        <main className="mobile-editor__trainer-editor">
          <h3 className="mobile-editor__trainer-editor-title">
            Вопрос {currentQuestionIndex + 1} из {questions.length}
          </h3>

          {/* Редактор вопроса */}
      <Card className="mobile-editor__card">
        <CardContent className="mobile-editor__question-content">
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Текст вопроса (опционально)"
            value={currentQuestion.questionText}
            onChange={(e) => updateQuestion({ questionText: e.target.value })}
            className="mobile-editor__field"
            margin="normal"
            placeholder="Введите текст вопроса"
          />

          <div className="mobile-editor__mode-selector mobile-editor__trainer-media-selector">
            <label className="mobile-editor__mode-label">
              Медиа к вопросу
            </label>
            <ToggleButtonGroup
              fullWidth
              value={currentQuestionMode === "text" ? null : currentQuestionMode}
              exclusive
              onChange={(_, newMode) =>
                updateQuestion({ questionMode: newMode || "text" })
              }
              size="small"
              aria-label="Медиа к вопросу"
            >
              <ToggleButton value="video" title="Видео" aria-label="Видео">
                <VideocamOutlinedIcon fontSize="small" />
                <span>Видео</span>
              </ToggleButton>
              <ToggleButton value="image" title="Картинка" aria-label="Картинка">
                <ImageOutlinedIcon fontSize="small" />
                <span>Картинка</span>
              </ToggleButton>
            </ToggleButtonGroup>
            <p className="mobile-editor__trainer-media-hint">
              При необходимости добавьте видео или изображение к тексту вопроса.
            </p>
          </div>

          {/* Видео */}
          {currentQuestionMode === "video" && (
            <>
              {currentQuestion.hasVideo ? (
                <div className="mobile-editor__video-preview">
                  <div className="mobile-editor__video-placeholder">
                    <VideocamOutlinedIcon sx={{ fontSize: 48 }} />
                    <p>Видео загружено</p>
                  </div>
                  <Button
                    size="small"
                    startIcon={<VideocamOutlinedIcon />}
                    onClick={() =>
                      document.getElementById("video-input").click()
                    }
                  >
                    Изменить видео
                  </Button>
                  <input
                    id="video-input"
                    type="file"
                    accept="video/*"
                    onChange={handleVideoUpload}
                    style={{ display: "none" }}
                  />
                </div>
              ) : (
                <InputFileUpload
                  onChange={handleVideoUpload}
                  accept="video/*"
                  label="Загрузить видео"
                  disabled={isLoadingVideo}
                />
              )}
            </>
          )}

          {/* Изображение */}
          {currentQuestionMode === "image" && (
            <>
              {currentQuestion.hasImage ? (
                <div className="mobile-editor__image-preview">
                  <div className="mobile-editor__image-placeholder">
                    <ImageOutlinedIcon sx={{ fontSize: 48 }} />
                    <p>Изображение загружено</p>
                  </div>
                  <Button
                    size="small"
                    startIcon={<ImageOutlinedIcon />}
                    onClick={() =>
                      document.getElementById("image-input").click()
                    }
                  >
                    Изменить изображение
                  </Button>
                  <input
                    id="image-input"
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: "none" }}
                  />
                </div>
              ) : (
                <InputFileUpload
                  onChange={handleImageUpload}
                  accept="image/*"
                  label="Загрузить изображение"
                  disabled={isLoadingImage}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Варианты ответов */}
      <div className="mobile-editor__answers-section">
        <h3 className="mobile-editor__section-title">
          Варианты ответов ({currentQuestion.answers.length})
        </h3>

        {currentQuestion.answers.map((answer, answerIndex) => (
          <Card key={answer.id} className="mobile-editor__answer-item">
            <CardContent className="mobile-editor__answer-content">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={answer.isCorrect}
                    onChange={(e) =>
                      updateAnswer(answerIndex, {
                        isCorrect: e.target.checked,
                      })
                    }
                  />
                }
                label="Правильный ответ"
                className="mobile-editor__answer-checkbox"
              />
              <TextField
                fullWidth
                size="small"
                label={`Ответ ${answerIndex + 1}`}
                value={answer.label}
                onChange={(e) =>
                  updateAnswer(answerIndex, {
                    label: e.target.value,
                  })
                }
                margin="normal"
              />
              {currentQuestion.answers.length > 2 && (
                <IconButton
                  size="small"
                  onClick={() => {
                    const updatedAnswers = currentQuestion.answers.filter(
                      (_, idx) => idx !== answerIndex,
                    );
                    updateQuestion({ answers: updatedAnswers });
                  }}
                  color="error"
                  className="mobile-editor__delete-answer"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              )}
            </CardContent>
          </Card>
        ))}

        {currentQuestion.answers.length < 5 && (
          <Button
            fullWidth
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => {
              const newAnswer = {
                id: `${currentQuestion.id}-a${Date.now()}`,
                label: "",
                isCorrect: false,
              };
              updateQuestion({
                answers: [...currentQuestion.answers, newAnswer],
              });
            }}
            className="mobile-editor__btn-add-answer"
          >
            Добавить ответ
          </Button>
        )}
      </div>

      {questions.length > 1 && (
        <div className="mobile-editor__question-actions">
          <Button
            fullWidth
            variant="outlined"
            color="error"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => setShowDeleteConfirm("question")}
          >
            Удалить вопрос
          </Button>
        </div>
      )}
        </main>
      </div>

      {/* Dialog: Confirm delete question */}
      <Dialog
        open={showDeleteConfirm === "question"}
        onClose={() => setShowDeleteConfirm(null)}
      >
        <DialogTitle>Удалить вопрос?</DialogTitle>
        <DialogContent>
          <p>
            Вы уверены, что хотите удалить этот вопрос? Это действие нельзя
            отменить.
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirm(null)}>Отмена</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteQuestion}
          >
            Удалить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Settings */}
      <Dialog
        open={showSettings}
        onClose={() => setShowSettings(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Настройки проекта</DialogTitle>
        <DialogContent>
          <p>
            Для изменения полных настроек проекта перейдите в настройки на
            десктопной версии.
          </p>
          <p style={{ fontSize: "0.9em", color: "#666" }}>
            В мобильной версии доступно редактирование вопросов и ответов.
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSettings(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
